import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, MessageSquare } from 'lucide-react';

import { BUSINESS_DEFAULT } from '../lib/businessInfo';

const LOGO = "https://customer-assets.emergentagent.com/job_buraq-transport/artifacts/0a9wh5vw_0E081036-6309-4011-9127-0A9FE497D0F8.png";

// Legal / brand name used in the SMS consent clause carriers verify during A2P 10DLC registration.
const COMPANY = "RideBuraq";

const SECTIONS = [
  {
    title: '1. Information We Collect',
    body: `We collect the information you provide when you create an account or book a ride — your name, email address, phone number, pickup and drop-off locations, ride preferences, and payment details processed securely by our payment provider. We also collect device information and push-notification tokens (used to send you trip alerts), the content of in-app messages you exchange with your chauffeur or dispatch, and records of calls connected through the Platform. We collect basic usage and device information to operate and improve the ${COMPANY} platform.`,
  },
  {
    title: '2. How We Use Your Information',
    body: 'We use your information to provide and manage your bookings, dispatch chauffeurs, connect you with your chauffeur through in-app messaging and masked phone calls, process payments, send booking confirmations, service updates, and push notifications, provide customer support, and comply with legal obligations. We do not sell your personal information.',
  },
  {
    title: '4. Calls & In-App Messaging',
    body: `To help you coordinate a ride, the Platform lets you message and call your assigned chauffeur. In-app messages are stored by ${COMPANY} and may be reviewed by our dispatch team for safety, support, and dispute resolution. Phone calls are connected through a third-party telephony provider using a relay number, so your personal phone number and the chauffeur's are kept private from each other; that provider processes the call connection and related metadata in order to place the call. Standard carrier voice and data rates may apply.`,
  },
  {
    title: '5. How We Share Information',
    body: `We share information only as needed to provide the service — for example, sharing pickup details and your in-app messages with your assigned chauffeur, connecting calls through our telephony provider, and processing payment data with our payment processor. Our dispatch team may access ride messages for safety and support. ${COMPANY} does not sell your personal information and does not share it with third parties for their own marketing.`,
  },
  {
    title: '6. Data Retention & Security',
    body: 'We retain your information for as long as your account is active or as needed to provide the service and meet legal, tax, and audit requirements. We use industry-standard safeguards to protect your data, though no method of transmission or storage is completely secure.',
  },
  {
    title: '7. Your Choices & Rights',
    body: 'You may access, update, or delete your account information at any time from your profile, or by contacting us. For text messages, you can opt out at any time by replying STOP to any message; reply HELP for help. Opting out of text messages does not affect other communications required to deliver your booking.',
  },
  {
    title: '8. Contact Us',
    body: `${COMPANY} is operated by ${BUSINESS_DEFAULT.name}, ${BUSINESS_DEFAULT.address}. Questions about this Privacy Policy or your data can be sent to privacy@rideburaq.com. This policy may be updated from time to time; continued use of the ${COMPANY} platform after changes take effect constitutes acceptance of the revised policy.`,
  },
];

const Privacy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      <div className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 w-[520px] h-[520px] rounded-full bg-orange-600/15 blur-[120px]" />

      <div className="relative w-full max-w-3xl mx-auto px-5 lg:px-8 pb-16">
        <header className="flex items-center justify-between py-6 lg:py-8">
          <button
            data-testid="back-btn"
            onClick={() => navigate(-1)}
            className="w-11 h-11 rounded-full border border-white/10 flex items-center justify-center text-gray-300 hover:text-white hover:border-orange-500/40 active:scale-95 transition-all"
            aria-label="Back"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <button
            data-testid="logo-home"
            onClick={() => navigate('/')}
            className="flex items-center gap-2.5 active:scale-95 transition-transform"
            aria-label="Home"
          >
            <img src={LOGO} alt="RideBuraq" className="w-8 h-8 object-contain" />
            <span className="text-[11px] font-semibold tracking-[0.35em] text-gray-400 uppercase">RideBuraq</span>
          </button>
        </header>

        <div className="pt-4 pb-8">
          <p className="text-[11px] tracking-[0.3em] uppercase text-orange-400/80 mb-2">Privacy & Legal</p>
          <h1 className="text-4xl lg:text-5xl font-semibold tracking-tight leading-none">Privacy Policy</h1>
        </div>

        {/* SMS / Text messaging consent — required for carrier (A2P 10DLC) approval */}
        <div data-testid="sms-consent" className="rounded-2xl border border-orange-500/20 bg-orange-500/5 p-5 mb-8">
          <div className="flex items-center gap-2 mb-3">
            <MessageSquare className="h-5 w-5 text-orange-400 shrink-0" />
            <h2 className="text-lg font-semibold text-white">3. Text Messaging (SMS) &amp; Mobile Information</h2>
          </div>
          <p className="text-sm text-gray-300 leading-relaxed mb-3" data-testid="sms-no-sale">
            We do not sell or share your mobile number or text-message opt-in information with third parties for marketing purposes. Message and data rates may apply. Reply STOP to opt out of texts, HELP for help.
          </p>
          <p className="text-sm text-gray-300 leading-relaxed mb-3">
            {BUSINESS_DEFAULT.name} does not share mobile numbers, text messaging originator opt-in data, or consent with
            any third parties or affiliates for marketing or promotional purposes. Text messaging originator
            opt-in data and consent will not be shared with any third parties, excluding aggregators and
            providers of the Text Message services.
          </p>
          <p className="text-sm text-gray-400 leading-relaxed">
            By providing your mobile number and opting in, you consent to receive booking-related and
            account text messages from {BUSINESS_DEFAULT.name}. Message and data rates may apply. Message frequency varies.
            Reply STOP to unsubscribe at any time, or HELP for assistance.
          </p>
        </div>

        <div className="space-y-6">
          {SECTIONS.map((s, i) => (
            <div key={i}>
              <h2 className="text-lg font-semibold text-white mb-2">{s.title}</h2>
              <p className="text-sm text-gray-400 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>

        <p className="text-xs text-gray-600 mt-10">
          This Privacy Policy is provided as a general template and does not constitute legal advice. Please
          have it reviewed by a licensed attorney before relying on it.
        </p>
      </div>
    </div>
  );
};

export default Privacy;
